import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useUpdateProject } from "@/modules/work/projects/project/crud/update/mutation";
import { eventBus } from "utils/eventBus";
import { UserFieldInput } from "permissions/components/user-field-input";
import ProjectNameCell from "@/modules/work/projects/cells/name";
import { formatName } from "utils/index";
import { ROUTE } from "@/lib/utils";

export default function SalesCoordinatorList() {
  const { employeeId } = useParams();
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };
  const permissions = usePermissions();
  const canReadClient = permissions.hasCollectionPermission("clients", "read");
  const canEditCoordinator = permissions.hasFieldAccess("projects", "update", "sales_coordinator");

  const storageKey = employeeId ? `profile-sc-projects-${employeeId}` : "profile-sc-projects";

  const updateProjectMutation = useUpdateProject();

  const handleUpdateSuccess = () => {
    eventBus.emit("refresh:list", `projects${storageKey}`);
  };

  const baseFields = [
    "id",
    "name",
    "project_id",
    "status",
    "logo",
    "sales_coordinator.id",
    "sales_coordinator.first_name",
    "sales_coordinator.last_name",
    "sales_coordinator.avatar",
  ];

  const fields = [
    ...baseFields,
    ...(canReadClient
      ? ["client.first_name", "client.last_name", "client.company_id.name"]
      : []),
  ];

  const columns = [
    {
      id: "name",
      header: "Project",
      accessorKey: "name",
      cell: ProjectNameCell
    },
    {
      id: "client",
      header: "Client",
      accessorKey: "client",
      collection: "clients",
      cell: ({ getValue }) => {
        return formatName(getValue())
      },
    },
    {
      id: "sales_coordinator",
      header: "SC",
      hidden: !canEditCoordinator,
      href: false,
      cell: ({ row }) => {
        const project = row.original;
        return (
          <UserFieldInput
            targetItem={project.id}
            field="sales_coordinator"
            collection="projects"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="sales_coordinating"
              value={project.sales_coordinator}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateProjectMutation.mutateAsync({
                        projectId: project.id,
                        values: {
                          sales_coordinator: selectedEmployee,
                        },
                      });
                      handleUpdateSuccess();
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    sales_coordinator: userFilter,
    status: { _eq: "active" },
  };

  return (
    <List
      title="Projects"
      storageKey={storageKey}
      endPoint="projects"
      filters={filters}
      columns={columns.filter(
        (col) => !col.hidden && (!col.collection || permissions.hasCollectionPermission(col.collection, "read"))
      )}
      meta={{ fields }}
      href={(item) => ROUTE.PROJECT(item.project_id)}
    />
  );
}
